import { Extension } from '@tiptap/core';
import { Plugin } from 'prosemirror-state';

export interface EditedByUserExtensionOptions {
	userId: string;
	saveAttribution: boolean;
}

export const EditedByUserExtension = Extension.create<EditedByUserExtensionOptions>({
	name: 'editedByUser',

	addOptions() {
		return {
			userId: '',
			saveAttribution: false,
		};
	},

	addGlobalAttributes() {
		// Only add attributes if attribution is enabled
		if (!this.options.saveAttribution) {
			return [];
		}

		return [
			{
				types: ['paragraph', 'heading'],
				attributes: {
					'data-edited-by': {
						default: null,
						parseHTML: (el) => el.getAttribute('data-edited-by'),
						renderHTML: (attrs) => (attrs['data-edited-by'] ? { 'data-edited-by': attrs['data-edited-by'] } : {}),
					},
				},
			},
		];
	},

	addStorage() {
		return {
			nodeContent: new Map<string, string>(), // Track node content by node-id
			trackedNodes: new Set<string>(), // Track which nodes we've seen before (same as created-by extension)
		};
	},

	addProseMirrorPlugins() {
		return [
			new Plugin({
				appendTransaction: (transactions, oldState, newState) => {
					const { userId, saveAttribution } = this.options;
					if (!userId || !saveAttribution || !transactions.some((tx) => tx.docChanged)) {
						return null;
					}

					const tr = newState.tr;
					let modified = false;

					// Initialize storage if it doesn't exist
					if (!this.editor.storage.nodeContent) {
						this.editor.storage.nodeContent = new Map<string, string>();
					}
					if (!this.editor.storage.trackedNodes) {
						this.editor.storage.trackedNodes = new Set<string>();
					}
					const nodeContent = this.editor.storage.nodeContent as Map<string, string>;
					const trackedNodes = this.editor.storage.trackedNodes as Set<string>;

					// Track IDs seen in this transaction to detect duplicates from splits
					const idsInThisTransaction = new Set<string>();

					// Helper function to add user to edited-by list
					const addUserToEditedBy = (existingEditedBy: string | null, currentUserId: string): string => {
						if (!existingEditedBy) {
							return currentUserId;
						}

						const editors = existingEditedBy.split(',').map((id) => id.trim());
						if (!editors.includes(currentUserId)) {
							return `${existingEditedBy},${currentUserId}`;
						}
						return existingEditedBy;
					};

					// Helper function to get node text content
					const getNodeTextContent = (node: any): string => {
						return node.textContent || '';
					};

					newState.doc.descendants((node, pos) => {
						if (!node.isBlock || !['paragraph', 'heading'].includes(node.type.name)) {
							return true;
						}

						const nodeId = node.attrs['data-node-id'];
						const createdBy = node.attrs['data-created-by'];
						const currentEditedBy = node.attrs['data-edited-by'];

						// Only track edits for nodes that already exist (have node-id and created-by)
						if (!nodeId || !createdBy) {
							return true;
						}

						// Check for duplicate IDs in this transaction (indicates a split)
						const isDuplicateInTransaction = idsInThisTransaction.has(nodeId);

						if (isDuplicateInTransaction) {
							// This is a duplicate ID from a split
							// Clean up inherited data-edited-by and set to current user only
							// Since the current user is creating this new node (by splitting), they should be the only editor
							const newAttrs = {
								...node.attrs,
								'data-edited-by': userId,
							};
							tr.setNodeMarkup(pos, node.type, newAttrs);
							modified = true;
							// Skip further processing - CreatedByUserExtension will handle the new ID
							return true;
						}

						// Add to transaction tracking
						idsInThisTransaction.add(nodeId);

						// Check if this is an existing node we've tracked before
						const isExistingNode = trackedNodes.has(nodeId);

						if (isExistingNode) {
							// This is a node we've seen before - check for content changes
							const currentContent = getNodeTextContent(node);
							const previousContent = nodeContent.get(nodeId);

							// If content has actually changed, this is an edit
							if (previousContent !== undefined && previousContent !== currentContent) {
								const newEditedBy = addUserToEditedBy(currentEditedBy, userId);

								// Only update if the user isn't already in the list
								if (newEditedBy !== currentEditedBy) {
									const newAttrs = {
										...node.attrs,
										'data-edited-by': newEditedBy,
									};
									tr.setNodeMarkup(pos, node.type, newAttrs);
									modified = true;
								}
							}

							// Update stored content
							nodeContent.set(nodeId, currentContent);
						} else {
							// This is the first time we're seeing this node
							trackedNodes.add(nodeId);

							// Store initial content
							const currentContent = getNodeTextContent(node);
							nodeContent.set(nodeId, currentContent);

							// Handle attribution for newly discovered nodes
							if (createdBy === userId) {
								// This node was created by the current user
								// Check if current edited-by list has more than just the creator
								if (currentEditedBy) {
									const editors = currentEditedBy.split(',').map((id: string) => id.trim());
									const hasOtherEditors = editors.length > 1 || (editors.length === 1 && editors[0] !== userId);

									if (hasOtherEditors) {
										// New node has inherited incorrect data-edited-by - reset to just creator
										const newAttrs = {
											...node.attrs,
											'data-edited-by': userId,
										};
										tr.setNodeMarkup(pos, node.type, newAttrs);
										modified = true;
									}
								} else {
									// New node missing data-edited-by - initialize with creator
									const newAttrs = {
										...node.attrs,
										'data-edited-by': userId,
									};
									tr.setNodeMarkup(pos, node.type, newAttrs);
									modified = true;
								}
							} else if (createdBy && !currentEditedBy && currentContent.trim() !== '') {
								// This looks like an existing document node created by someone else that predates edit tracking
								const newAttrs = {
									...node.attrs,
									'data-edited-by': createdBy,
								};
								tr.setNodeMarkup(pos, node.type, newAttrs);
								modified = true;
							}
						}

						return true;
					});

					return modified ? tr : null;
				},
			}),
		];
	},
});
